import dayjs from 'dayjs'
import type {
  MdCase,
  MdCaseAlertItem,
  MdCasePiTagReference,
  MdCaseStatus,
} from 'features/mdCases/mdCase'
import { isMdCaseStatus } from 'features/mdCases/mdCase'
import { isMdCaseDocumentReferenceUrl } from 'features/mdCases/mdCaseReferences'
import type { ResolvedLinkedAlert } from 'features/mdCases/selectors/mdCaseLinkedAlerts'

export type MdCaseReferenceOption =
  | {
      key: string
      kind: 'pi-tag'
      label: string
      reference: MdCasePiTagReference
    }
  | {
      key: string
      kind: 'document'
      label: string
      reference: string
    }

export type PiTagReferenceDraftState = {
  piTagId: string
  startAt: string
  endAt: string
}

export type MdCaseAlertTimeRange = {
  start: string
  end: string
}

export type MdCaseDocumentReferenceKind = 'routeable-document' | 'url' | 'text'
export type MdCaseDetailsSectionId =
  | 'investigation'
  | 'recommendation'
  | 'activity'
export type MdCaseDetailsSectionPosition = {
  id: MdCaseDetailsSectionId
  top: number
}

const REFERENCE_EMPTY_VALUE = 'Not provided'

const hasValue = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0

const getCompleteTimeRange = (
  start: string | null | undefined,
  end: string | null | undefined
): MdCaseAlertTimeRange | null =>
  hasValue(start) && hasValue(end) ? { start, end } : null

const getTimestamp = (value: string): number | null => {
  const timestamp = Date.parse(value)
  return Number.isNaN(timestamp) ? null : timestamp
}

const isAlertTimeRange = (
  range: MdCaseAlertTimeRange | null
): range is MdCaseAlertTimeRange => range !== null

export const getPiTagReferenceKey = (reference: MdCasePiTagReference) =>
  `pi-tag:${reference.id}`

export const getDocumentReferenceKey = (document: string, index: number) =>
  `document:${index}:${document}`

const getPiTagReferenceLabel = (
  reference: MdCasePiTagReference,
  index: number
) => reference.pi_tag_id?.trim() || `PI Tag ${index + 1}`

const getDocumentReferenceLabel = (document: string, index: number) =>
  document.trim() || `Document ${index + 1}`

export const getRawTagValue = (mdCase: MdCase | null, tagKey: string) => {
  const rawTags = mdCase?.rawCase?.raw?.tags
  if (!rawTags || typeof rawTags !== 'object' || Array.isArray(rawTags)) {
    return null
  }

  const value = (rawTags as Record<string, unknown>)[tagKey]
  return typeof value === 'string' && value.trim().length ? value : null
}

export const getSelectionStateCopy = (
  kind: 'not-found' | 'not-md' | 'unusable-md'
): { title: string; description: string } => {
  switch (kind) {
    case 'not-found':
      return {
        title: 'Case not found',
        description:
          'The selected case could not be loaded for this site. It may have been deleted or moved.',
      }
    case 'not-md':
      return {
        title: 'Unsupported case type',
        description:
          'This detail page only supports M&D cases. The selected case is using a different workflow domain.',
      }
    case 'unusable-md':
      return {
        title: 'M&D case is missing required fields',
        description:
          'The case was identified as M&D, but the normalized detail shape is incomplete and cannot be edited here yet.',
      }
  }
}

export const getReferenceOptions = (
  mdCase: MdCase
): MdCaseReferenceOption[] => [
  ...(mdCase.references.pi_tag_items ?? []).map((reference, index) => ({
    key: getPiTagReferenceKey(reference),
    kind: 'pi-tag' as const,
    label: getPiTagReferenceLabel(reference, index),
    reference,
  })),
  ...mdCase.references.documents.map((document, index) => ({
    key: getDocumentReferenceKey(document, index),
    kind: 'document' as const,
    label: getDocumentReferenceLabel(document, index),
    reference: document,
  })),
]

export const getDefaultReferenceKey = (mdCase: MdCase): string | null => {
  const firstPiTagReference = mdCase.references.pi_tag_items?.[0]
  if (firstPiTagReference) {
    return getPiTagReferenceKey(firstPiTagReference)
  }

  const firstDocumentReference = mdCase.references.documents[0]
  return firstDocumentReference !== undefined
    ? getDocumentReferenceKey(firstDocumentReference, 0)
    : null
}

export const formatReferenceValue = (value: string | null) =>
  value?.trim().length ? value : REFERENCE_EMPTY_VALUE

export const formatLocalDateTimeInputValue = (value: string | null): string =>
  value ? dayjs(value).format('YYYY-MM-DDTHH:mm') : ''

export const parseLocalDateTimeInputValue = (value: string): string | null =>
  value.trim() ? new Date(value).toISOString() : null

export const getReferenceListMeta = (
  referenceOption: MdCaseReferenceOption
): string => {
  if (referenceOption.kind === 'document') {
    return isMdCaseDocumentReferenceUrl(referenceOption.reference)
      ? 'Link reference'
      : ''
  }

  const startAt = referenceOption.reference.start_at ?? 'Unknown start'
  const endAt = referenceOption.reference.end_at ?? 'Unknown end'
  return `${startAt} -> ${endAt}`
}

const ROUTEABLE_DOCUMENT_REFERENCE_PATTERNS = [
  /^DOC-[A-Za-z0-9-]+$/i,
  /^system-document-[A-Za-z0-9-]+$/i,
  /^report-[A-Za-z0-9-]+$/i,
]

export const getMdCaseDocumentReferenceKind = (
  reference: string
): MdCaseDocumentReferenceKind => {
  if (isMdCaseDocumentReferenceUrl(reference)) {
    return 'url'
  }

  return ROUTEABLE_DOCUMENT_REFERENCE_PATTERNS.some((pattern) =>
    pattern.test(reference.trim())
  )
    ? 'routeable-document'
    : 'text'
}

const getFallbackAlertTimeRange = (
  fallbackAlert:
    | Pick<
        MdCaseAlertItem,
        'start_at' | 'end_at' | 'focus_start_at' | 'focus_end_at'
      >
    | null
    | undefined
): MdCaseAlertTimeRange | null =>
  getCompleteTimeRange(
    fallbackAlert?.start_at ?? null,
    fallbackAlert?.end_at ?? null
  ) ??
  getCompleteTimeRange(
    fallbackAlert?.focus_start_at ?? null,
    fallbackAlert?.focus_end_at ?? null
  )

const getResolvedAlertRenderTimeRange = (
  resolvedAlert: ResolvedLinkedAlert['resolvedAlert']
): MdCaseAlertTimeRange | null =>
  getCompleteTimeRange(
    resolvedAlert?.data.render_start_at ?? null,
    resolvedAlert?.data.render_end_at ?? null
  )

const getResolvedAlertFocusTimeRange = (
  resolvedAlert: ResolvedLinkedAlert['resolvedAlert']
): MdCaseAlertTimeRange | null =>
  getCompleteTimeRange(
    resolvedAlert?.data.focus_start_at ?? null,
    resolvedAlert?.data.focus_end_at ?? null
  )

const getAggregateAlertTimeRange = (
  alertRanges: readonly MdCaseAlertTimeRange[]
): MdCaseAlertTimeRange | null => {
  if (!alertRanges.length) {
    return null
  }

  const alertRangesWithTimestamps = alertRanges
    .map((range) => ({
      ...range,
      startTimestamp: getTimestamp(range.start),
      endTimestamp: getTimestamp(range.end),
    }))
    .filter(
      (
        range
      ): range is MdCaseAlertTimeRange & {
        startTimestamp: number
        endTimestamp: number
      } =>
        range.startTimestamp !== null &&
        range.endTimestamp !== null &&
        range.startTimestamp <= range.endTimestamp
    )

  if (!alertRangesWithTimestamps.length) {
    return alertRanges[0]
  }

  const earliestRange = alertRangesWithTimestamps.reduce((earliest, range) =>
    range.startTimestamp < earliest.startTimestamp ? range : earliest
  )
  const latestRange = alertRangesWithTimestamps.reduce((latest, range) =>
    range.endTimestamp > latest.endTimestamp ? range : latest
  )

  return {
    start: earliestRange.start,
    end: latestRange.end,
  }
}

export const getMdCaseAlertItemsTimeRange = (
  alertItems: readonly MdCaseAlertItem[] | null | undefined
): MdCaseAlertTimeRange | null =>
  getAggregateAlertTimeRange(
    (alertItems ?? []).map(getFallbackAlertTimeRange).filter(isAlertTimeRange)
  )

export const getLinkedAlertTimeRange = (
  linkedAlert: ResolvedLinkedAlert
): MdCaseAlertTimeRange | null => {
  return (
    getResolvedAlertRenderTimeRange(linkedAlert.resolvedAlert) ??
    getCompleteTimeRange(
      linkedAlert.fallbackAlert?.start_at ?? null,
      linkedAlert.fallbackAlert?.end_at ?? null
    ) ??
    getResolvedAlertFocusTimeRange(linkedAlert.resolvedAlert) ??
    getCompleteTimeRange(
      linkedAlert.fallbackAlert?.focus_start_at ?? null,
      linkedAlert.fallbackAlert?.focus_end_at ?? null
    )
  )
}

export const getCaseAlertTimeRange = (
  linkedAlerts: readonly ResolvedLinkedAlert[]
): MdCaseAlertTimeRange | null =>
  getAggregateAlertTimeRange(
    linkedAlerts.map(getLinkedAlertTimeRange).filter(isAlertTimeRange)
  )

export const getActiveMdCaseDetailsSection = (
  sections: readonly MdCaseDetailsSectionPosition[],
  scrollTop: number,
  stickyHeaderHeight: number
): MdCaseDetailsSectionId => {
  if (!sections.length) {
    return 'investigation'
  }

  const activationTop = scrollTop + Math.max(stickyHeaderHeight, 0) + 12
  const sortedSections = [...sections].sort(
    (left, right) => left.top - right.top
  )

  return sortedSections.reduce<MdCaseDetailsSectionId>(
    (activeSectionId, section) =>
      section.top <= activationTop ? section.id : activeSectionId,
    sortedSections[0].id
  )
}

export const getPiTagReferenceDraftState = (
  mdCase: MdCase,
  selectedReference: MdCaseReferenceOption | null
): PiTagReferenceDraftState => {
  const selectedPiTagReference =
    selectedReference?.kind === 'pi-tag' ? selectedReference.reference : null
  const fallbackAlert = mdCase.alerts.items?.[0] ?? null

  return {
    piTagId:
      selectedPiTagReference?.pi_tag_id ??
      mdCase.references.pi_tag ??
      fallbackAlert?.pi_tag_id ??
      '',
    startAt: formatLocalDateTimeInputValue(
      selectedPiTagReference?.start_at ?? fallbackAlert?.start_at ?? null
    ),
    endAt: formatLocalDateTimeInputValue(
      selectedPiTagReference?.end_at ?? fallbackAlert?.end_at ?? null
    ),
  }
}

export const isStatusOption = (value: string): value is MdCaseStatus =>
  isMdCaseStatus(value)
